import {
  Microphone,
  PaperPlaneRightIcon,
  PhoneIcon,
} from "@phosphor-icons/react";
import { useEffect, useRef } from "react";
import { usePublicTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface ChatInputProps {
  textInput: string;
  setTextInput: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  /** A message is in flight: sending is refused, typing is not. */
  disabled: boolean;
  agentName?: string;
  phoneNumber?: string | null;
  /** Hide the "Propulsé par HeyUp" footer (branding option). */
  hidePoweredBy?: boolean;
  // "Call here" = in-browser web audio to the agent. When enabled (and a handler is
  // provided), the phone button offers it alongside dialing the number.
  webCallEnabled?: boolean;
  onStartAudio?: () => void;
}

/**
 * The round send button of both composers — the chat's and the one under a call —
 * so they agree on size and, above all, on what "nothing to send yet" looks like:
 * the call bar's used to fade to 30% while this one went grey.
 */
export function SendButton({
  className,
  ...props
}: React.ComponentProps<"button">) {
  const { t } = usePublicTranslation();
  return (
    <button
      title={t("chat.send")}
      aria-label={t("chat.send")}
      className={cn(
        "flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90 active:scale-95 disabled:bg-muted disabled:text-muted-foreground/60",
        className,
      )}
      {...props}
    >
      <PaperPlaneRightIcon className="size-4" weight="fill" />
    </button>
  );
}

const phoneBtnClass =
  "flex size-9 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground";

export function ChatInput({
  textInput,
  setTextInput,
  onSubmit,
  disabled,
  agentName,
  phoneNumber,
  hidePoweredBy,
  webCallEnabled,
  onStartAudio,
}: ChatInputProps) {
  const { t } = usePublicTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // The field is **never disabled**, and that is the whole fix for the keyboard folding
  // away after every message.
  //
  // It used to be disabled for the round-trip, which blurs it — and then refocused from
  // an effect once the answer landed. That works on a desktop and cannot work on a
  // phone: iOS only opens the keyboard for a `focus()` that happens inside a user
  // gesture, and the reply comes back long after the tap is over. So the visitor typed,
  // sent, and watched the keyboard close; writing the next message meant tapping the
  // field again, every single time.
  //
  // Nothing needed the disabled state anyway: sending is gated on `disabled` in the
  // handlers below, and a visitor who types ahead while the agent is answering is
  // behaving perfectly reasonably — their text is still there when the round-trip ends.
  //
  // Focus on *mount* stays a desktop-only decision: right where the conversation opens
  // ready to type, wrong on a phone, where it would pop the keyboard over a thread
  // nobody has read yet.
  useEffect(() => {
    if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
      textareaRef.current?.focus();
    }
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = "auto";

    // Calculate max height (4 lines * line-height + padding)
    const lineHeight = parseInt(getComputedStyle(textarea).lineHeight);
    const maxHeight = lineHeight * 4;

    // Set height based on content, capped at max height
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = `${newHeight}px`;
  }, [textInput]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!textInput.trim() || disabled) return;

    // Synchronously, inside the tap: pressing the send button moves the focus to it,
    // and on iOS the keyboard only survives if the field takes it straight back — in
    // the gesture, never in the promise below.
    textareaRef.current?.focus();

    await onSubmit(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter (allow Shift+Enter for new line)
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const canSend = !!textInput.trim() && !disabled;

  const canWebCall = !!webCallEnabled && !!onStartAudio;
  const hasNumber = !!phoneNumber;
  const callNumber = () => {
    window.location.href = `tel:${phoneNumber}`;
  };

  // The phone button: a menu when both "call the number" and "call here" are available,
  // otherwise a single direct action. Nothing when neither is offered.
  const renderPhoneControl = () => {
    if (canWebCall && hasNumber) {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" title={t("chat.call")} className={phoneBtnClass}>
              <PhoneIcon className="size-5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="top">
            <DropdownMenuItem onClick={callNumber}>
              <PhoneIcon className="mr-2 size-4" />
              {t("chat.call_number")}
              <span className="ml-2 text-xs text-muted-foreground">
                {phoneNumber}
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onStartAudio}>
              <Microphone className="mr-2 size-4" />
              {t("chat.call_here")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }
    if (hasNumber) {
      return (
        <button
          type="button"
          onClick={callNumber}
          title={t("chat.call_number_named", { phone: phoneNumber })}
          className={phoneBtnClass}
        >
          <PhoneIcon className="size-5" />
        </button>
      );
    }
    if (canWebCall) {
      return (
        <button
          type="button"
          onClick={onStartAudio}
          title={t("chat.talk_audio")}
          className={phoneBtnClass}
        >
          <PhoneIcon className="size-5" />
        </button>
      );
    }
    return null;
  };

  return (
    <div className="border-t bg-background/90 px-3 py-3 backdrop-blur">
      {/* One bordered pill holds every control, so the phone icon, the textarea and
          the send button share a baseline and stay aligned on one and many lines. */}
      <form
        onSubmit={handleSubmit}
        className="flex items-end gap-1.5 rounded-[var(--chat-radius,1rem)] border bg-background p-1.5 transition-colors focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/15"
      >
        {renderPhoneControl()}

        <textarea
          ref={textareaRef}
          value={textInput}
          onChange={(e) => setTextInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            agentName
              ? t("chat.placeholder_named", { name: agentName })
              : t("chat.placeholder")
          }
          /* 16px on a phone, and not for taste: Safari zooms the whole page in when
             a focused field is smaller than that, which on the embedded widget
             scrolls the host's site sideways under the sheet and leaves the visitor
             pinch-zooming their way back. `sm:text-sm` restores the smaller type
             everywhere a keyboard is not about to appear. */
          className="max-h-[104px] min-h-[36px] flex-1 resize-none self-center border-0 bg-transparent px-2 py-2 text-base leading-5 outline-none placeholder:text-muted-foreground sm:text-sm [-webkit-touch-callout:none]"
          rows={1}
          inputMode="text"
        />

        <SendButton type="button" disabled={!canSend} onClick={handleSubmit} />
      </form>

      {!hidePoweredBy && (
        <p className="mt-2 text-center text-[11px] text-muted-foreground/70">
          {t("chat.powered_by")}{" "}
          <a
            href="https://heyup.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-foreground transition-colors hover:text-primary"
          >
            HeyUp
          </a>
        </p>
      )}
    </div>
  );
}
